import { Optional, Component, OnInit, AfterViewInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, forwardRef } from '@angular/core';
import { Output, EventEmitter } from '@angular/core';
import { ViewChild } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { ControlContainer, ControlValueAccessor }  from "@angular/forms";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

import { Subscription } from 'rxjs';

import { platformService } from '@unpispas/upp-base';

// refs: https://almerosteyn.com/2016/04/linkup-custom-control-to-ngcontrol-ngmodel

interface ErrorInfo {
    [key: string]: string;
}

/*************************************/
/* UPP-TEXTAREA KEYBOARD COMPONENT   */
/*************************************/

/**
 * A custom Angular component for keyboard-based input.
 * Supports integration with Angular Forms through `ControlValueAccessor`.
 * Provides custom behavior for `price` and `number` types, and supports kiosk mode.
 *
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
    selector: 'upp-kb-textarea',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./kb/upp-textarea.html',
    styleUrls: [ 
        './kb/upp-textarea.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppKbTextAreaComponent),
            multi: true
        }            
    ]
})
export class UppKbTextAreaComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
    /** Placeholder text for the input. */
    @Input() placeholder = '';
    /** Indicates if the input should be readonly. */
    @Input() readonly = false;
    /** Number of rows for the text area */
    @Input() rows = 1;
    /** Name of the input control. */
    @Input() name = 'none';
    /** Enables or disables kiosk mode for the input. */
    @Input() kiosk = true;
    /** Disables the input when set to true. */
    @Input() disabled = false;
    /** Configures autocomplete behavior for the input. */
    @Input() autocomplete = 'do-not-autofill';

    /** Emits the current value when it changes. */
    @Output() Changed = new EventEmitter<string | null>();
    /** Emits an event when the input gains focus. */
    @Output() Focused = new EventEmitter<void>(); 

    /** Reference to the native input element. */
    @ViewChild('input', {static: false}) _input: any;

    /** Internal flag indicating if the input is disabled. */
    public isDisabled = false;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback = (_v:any) => {
        // nothing to do (required by angular)
    };

    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    };
    /* eslint-enable @typescript-eslint/no-unused-vars */

    /** Internal value of the input. */
    private _innerValue: string | null | undefined = undefined;  

    /** Indicates if the component is in kiosk mode. */
    public _kiosk = false;
    /** Tracks if the input currently has focus. */
    public _focus = false;

    /**
     * Handles focus events and emits `uppFocus`.
     * @param value Whether the input has focus.
     */    
    onFocus(value: boolean): void{
        this._focus = value;
        this.Focused.emit();
    }

    /**
     * Native element reference for the input.
     * @returns {any} The parent node of the input element.
     */    
    get nativeElement(): any{
        if (!this._input){
            return null;
        }

        return this._input?.nativeElement.parentNode;
    }

    /**
     * Simulates a key press for the input element.
     * @param key The key to simulate.
     */    
    OnKeyPress(key: string): void{
        const _event = new KeyboardEvent("keyup", {
            key: key,
            shiftKey: false, // you don't need to include values
            ctrlKey: false,  // if you aren't going to use them.
            metaKey: false   // these are here for example's sake.
        })        

        this._onKeyUp(_event);
    }

    /**
     * Sets focus on the input element.
     */    
    SetFocus(): void{
        if (!this._input){
            return;
        }

        setTimeout(() => {
            this._input.nativeElement.focus();
            this._input.nativeElement.click();
        }, 50);
    }

    /**
     * Constructs an instance of `UppKbInputComponent`.
     *
     * @param {ChangeDetectorRef} change - A service to trigger manual change detection, ensuring the component reflects updates to its state.
     * @param {platformService} platform - A service to provide platform-specific functionality, such as kiosk detection.
     */    
    constructor(private change: ChangeDetectorRef, private platform: platformService) {
        // nothing to do
    }

    private _kiosk_subscription: Subscription | null = null;

    /**
     * Lifecycle hook. Called after the view is initialized.
     * Configures kiosk mode and subscribes to platform changes.
     */
    ngAfterViewInit(){
        this.platform.ready().
        then(() => {
            this._kiosk = this.kiosk && this.platform._isKiosk;
            this.change.markForCheck();
             
            if (this.kiosk){    // do not subscribe if kiosk is disabled
                this._kiosk_subscription = this.platform.OnKioskChanged.subscribe(
                () => {
                    this._kiosk = this.platform._isKiosk;
                    this.change.markForCheck();                
                });
            }
        });
    }

    /**
     * Lifecycle hook. Cleans up subscriptions on destroy.
     */    
    ngOnDestroy(){
        if (this._kiosk_subscription){
            this._kiosk_subscription.unsubscribe();
            this._kiosk_subscription = null;
        }
    }

    /**
     * Handles the `input` event for text-like input fields
     * Updates the internal value (`this.value`) when the user modifies the input field.
     * 
     * @param {Event} event - The `input` event triggered by the user.
     * @returns {void}
     */    
    onInput(event: Event): void {
        this.value = event.target ? (event.target as HTMLInputElement).value : '';
    }

    /**
     * Template keyup handler: text uses `onInput` for physical keys; only `_onKeyUp` via
     * synthetic `OnKeyPress` (on-screen pads).
     */
    onKeyUp(event: KeyboardEvent): void {
        void event;
    }

    /**
     * Updates the text area value from a pad-simulated key event.
     * @param event The keyboard event.
     */
    private _onKeyUp(event: KeyboardEvent): void {
        if (this.readonly){
            return;
        }

        if (event.key === 'Backspace'){
            this.value = this.value ? this.value.slice(0, -1) : '';
        }
        else if (event.key && event.key.length === 1){
            this.value = (this.value ?? '') + event.key;
        }
    }

    /**
     * Handles changes when in kiosk mode.
     * @param event The input event.
     */    
    onKiosk(event: Event): void{
        this.value = event.target ? (event.target as HTMLInputElement).value : '';
    }

    /********************************************/
    /* get/set accesors                         */
    /********************************************/

    @Input()

    /**
     * Gets the current value of the input.
     * This represents the internal state of the input field.
     * 
     * @returns {string | null} The current value of the input.
     */    
    
    get value(): string | null {
        return this._innerValue || null;;
    }

    /**
     * Sets a new value for the input.
     * This updates the internal state, triggers the `onChange` and `onTouch` callbacks, 
     * and emits the `uppChanged` event.
     * 
     * @param {string | null} v - The new value to set.
     */    
    set value(v: string | null) {
        const _firstcall = (this._innerValue === undefined);
        if (v !== this._innerValue) {
            this._innerValue = v;
            
            this._onChangeCallback(v);
            this._onTouchCallback();

            if (!_firstcall){
                this.Changed.emit(v);
            }
        }
    }    
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    /**
     * Writes a value to the component. Required by `ControlValueAccessor`.
     * @param value The new value to set.
     */    
    writeValue(value: any) {
        if (value !== this._innerValue) {
            this.value = value;
            this.change.markForCheck();
        }
    }

    /**
     * Registers a callback function to be called when the value changes.
     * @param fn The callback function.
     */    
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Registers a callback function to be called when the input is touched.
     * @param fn The callback function.
     */    
    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    }  
    
    /**
     * Sets the disabled state of the input.
     * @param isDisabled Whether the input should be disabled.
     */    
    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }     
}

/*************************************/
/* UPP-TEXTAREA ERROR COMPONENT      */
/*************************************/

/**
 * A component for displaying validation errors for form controls.
 * Automatically integrates with Angular reactive forms using `formControlName`.
 */
@Component({
    selector: 'upp-er-textarea',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./er/upp-textarea.html',
    styleUrls: [ 
        './er/upp-textarea.scss'
    ]
})
export class UppErTextAreaComponent implements OnChanges {
    /** The formGroup provided to the related upp-input. */
    @Input() form: FormGroup | null = null;
    /** The name of the form control to track errors for. */
    @Input() name: string | null = null;
    /** A mapping of error keys to their respective error messages. */
    @Input() errornfo: ErrorInfo | null = null
    /** Whether the error messages should be centered. */
    @Input() centered = false;
    /** Forces OnChanges to be called on value change. */
    @Input() value: string | null = null;

    /** The list of active error keys. */
    public errors: string[] = []
    
    /** The form control associated with this component. */
    public control: AbstractControl | null = null;

    /**
     * Constructor for dependency injection.
     */    
    constructor(){
        // nothing to do
    }

    /**
     * Lifecycle hook. Updates the list of errors when inputs change.
     */    
    ngOnChanges() {
        if (this.form && this.name){
            this.control = this.form.get(this.name);
        }

        if (this.control){
            this.errors = [];
            if (this.errornfo && this.control.errors){
                this.errors = Object.keys(this.control.errors).filter(key => Object.prototype.hasOwnProperty.call(this.errornfo, key))
            }    
        }
    }
}

/*************************************/
/* UPP-TEXTAREA COMPONENT            */
/*************************************/

/**
 * A reusable input component that supports Angular Forms integration,
 * custom validation, and advanced UI features like kiosk mode.
 *
 * @implements {ControlValueAccessor}
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
    selector: 'upp-textarea',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./upp-textarea.html',
    styleUrls: [ 
        './upp-textarea.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppTextAreaComponent),
            multi: true
        }            
    ]    
})
export class UppTextAreaComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    /** Placeholder text for the input. */
    @Input() placeholder = '';
    /** The name of the form control this input is bound to. */
    @Input() formControlName = '';
    /** Indicates if the input should be readonly. */
    @Input() readonly = false;
    /** Indicates if the input should be disabled. */
    @Input() disabled = false;
    /** The type of the input field (e.g., `text`, `number`). */
    @Input() kiosk = true;
    /** Number of rows for the text area */
    @Input() rows = 1;
    /** A title to display above the input. */
    @Input() title = '';
    /** Configures autocomplete behavior for the input. */
    @Input() autocomplete = 'off';    
    /** A mapping of error keys to their respective error messages. */
    @Input() errornfo: ErrorInfo = {} 
    /** Emits the current value when it changes. */
    @Output() Changed = new EventEmitter<string | null>(); 
    /** Emits an event when the input gains focus. */
    @Output() Focus = new EventEmitter<void>(); 

    @ViewChild('input', {static: false}) _input: any;   
    
    /** optional FromGroup */
    public formGroup: FormGroup | null = null
    public formControl: AbstractControl | null = null;

    private _innerValue: string | null | undefined = undefined;  

    /* eslint-disable @typescript-eslint/no-unused-vars */
    /** Callback to notify Angular Forms when the value changes. */
    private _onChangeCallback = (v: any) => {
        // nothing to do (required by angular)
    }
    /** Callback to notify Angular Forms when the input is touched. */
    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    }
    /* eslint-enable @typescript-eslint/no-unused-vars */

    private _showtitle = false;

    /**
     * Determines whether the input's title should be displayed.
     * @returns {boolean} True if the title should be displayed; otherwise, false.
     */    
    get showTitle(): boolean {
        return this._showtitle;
    }

    private _itemtitle: string | null = null;

    /**
     * Returns the processed title value.
     * @returns {string} The input's title.
     */    
    get viewTitle(): string {
        return this._itemtitle || '';
    }

    /**
     * Constructor for dependency injection.
     * @param controlContainer Provides access to the parent form group.
     * @param change A service to trigger manual change detection.
     */    
    constructor(@Optional() private controlContainer: ControlContainer, private change: ChangeDetectorRef) {
        // nothing to do
    }

    private _form_subscription: Subscription | null = null;

    /**
     * Lifecycle hook that initializes the component.
     * Sets up form control integration and initializes the value.
     */    
    ngOnInit() {
        this._innerValue = this.value;
        
        if (this.controlContainer && this.controlContainer instanceof FormGroupDirective){
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (this.formControl){
            this._innerValue = this.formControl.value || null;

            this._form_subscription = this.formControl.valueChanges.subscribe(
            () => {
                this.value = this.formControl?.value || null;
            });
        }

        this.onChange();
    }

    /**
     * Lifecycle hook that detects changes to the input's properties.
     */    
    ngOnChanges() {
        if (this._innerValue != undefined){
            this.onChange();
        }
    }
    
    /**
     * Lifecycle hook that cleans up resources on component destruction.
     * Unsubscribes from form control value changes.
     */
    ngOnDestroy() {
        if (this._form_subscription){
            this._form_subscription.unsubscribe();
            this._form_subscription = null;
        }        
    }

    /**
     * Handles key press events and delegates them to the input element.
     * @param key The key that was pressed.
     */    
    OnKeyPress(key: string) : void {
        if (this._input){
            this._input.OnKeyPress(key);
        }
    }

    /**
     * Sets focus on the input element.
     */    
    SetFocus(): void {
        if (this._input){
            this._input.SetFocus();
        }
    }

    /**
     * Handles value changes and updates the title visibility.
     * Emits a value change event.
     */
    onChange(): void {
        this._itemtitle = (this.title) ? this.title : this.placeholder;
        this._showtitle = (this._innerValue !== null) && (this._innerValue !== '');
        if (this._innerValue != undefined){
            this.Changed.emit(this._innerValue);
        }
        
        this.change.markForCheck();
    }

    /**
     * Emits a focus event when the input gains focus.
     */    
    onFocus(): void {
        this.Focus.emit();
    }

    /********************************************/
    /* disable control                          */
    /********************************************/

    /**
     * Determines whether the input is disabled.
     * @returns {boolean} True if the input is disabled; otherwise, false.
     */    
    get _disabled(): boolean {
        // if directly disabled, return true
        if (this.disabled){
            return true;
        }

        // check if the the form control is disabled
        const _formcontrol = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (_formcontrol){
            return _formcontrol.disabled;
        }

        // not disabled
        return false;
    }

    /********************************************/
    /* get/set accesors                         */
    /********************************************/
    
    @Input()

    /**
     * Gets the current value of the input.
     * @returns {string} The current value of the input.
     */    
    get value(): string | null {
        return this._innerValue || null;
    }

    /**
     * Sets a new value for the input.
     * Updates the internal value, notifies Angular Forms, and triggers value change events.
     * @param {string} v - The new value to set.
     */    
    set value(v: string | null) {
        if (v !== this._innerValue) {
            this._innerValue = v;

            this.onChange();

            this._onChangeCallback(v);
            this._onTouchCallback();
        }
    }    
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    /**
     * Writes a value to the input. Required by `ControlValueAccessor`.
     * @param {string} value - The value to write.
     */    
    writeValue(value: string) {
        if (value !== this._innerValue) {
            this.value = value;
            this.onChange();
        }        
    }

    /**
     * Registers a callback function to be called when the input value changes.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */    
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Registers a callback function to be called when the input is touched.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */    
    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    }    
}